// Helpers for projecting a picked WhatsApp contact onto an audience-board task.
//
// Used by:
//   - new-task-row-input.tsx (when the user adds multiple contacts via the
//     "+ Add contacts" button on the audience footer)
//   - whatsapp-id-cell.tsx (when the user edits an existing row's contact via
//     the cell-click dialog)
//
// Both flows split the contact's display name into first/last and build the
// same shape of customData. Centralising the rule keeps them in sync.

import { SelectedContact } from '@extension/context/content/components/shared';
import { TaskColumn } from '@common-models/task-column';
import { CustomColumnData } from '@common-models/task';

// Matches how a task actually stores custom columns (`Task.customData`), so a
// task's own customData can be read in as `base` and the result written back
// without a type mismatch. Writers here still emit `{ value: string }`, which
// is a valid CustomColumnData.
export type CustomData = Record<string, CustomColumnData>;

export interface NameColumns {
    firstNameColumn?: TaskColumn;
    lastNameColumn?: TaskColumn;
}

/**
 * Split a contact display name on whitespace: the first chunk is the first
 * name, everything else joined back together is the last name.
 *   "Joni Naor"          → { firstName: "Joni",  lastName: "Naor" }
 *   "John Q. Public"     → { firstName: "John",  lastName: "Q. Public" }
 *   "Madonna"            → { firstName: "Madonna", lastName: "" }
 *   ""                   → { firstName: "",     lastName: "" }
 */
export function splitDisplayName(displayName: string): { firstName: string; lastName: string } {
    const parts = (displayName ?? '').trim().split(/\s+/).filter(Boolean);
    return {
        firstName: parts[0] ?? '',
        lastName: parts.slice(1).join(' '),
    };
}

/**
 * Build a customData patch that sets the contact ID + first/last name cells.
 * Each column id is only included when the caller has the corresponding column
 * — boards may not have a First Name / Last Name column.
 */
export function buildContactCustomData({
    base,
    whatsappIdColumnId,
    contactId,
    displayName,
    firstNameColumn,
    lastNameColumn,
}: {
    base?: CustomData;
    whatsappIdColumnId: string;
    contactId: string;
    displayName: string;
} & NameColumns): CustomData {
    const { firstName, lastName } = splitDisplayName(displayName);
    const customData: CustomData = { ...(base || {}) };
    customData[whatsappIdColumnId] = { value: contactId };
    if (firstNameColumn?._id) customData[firstNameColumn._id] = { value: firstName };
    if (lastNameColumn?._id) customData[lastNameColumn._id] = { value: lastName };
    return customData;
}

/** Conventional resolver — the first/last name custom columns on an audience
 *  board are matched by a title that contains "first" / "last". Same heuristic
 *  the ImportNavButtons mapping-required check uses. */
export function findNameColumns(boardColumns: TaskColumn[]): NameColumns {
    return {
        firstNameColumn: boardColumns.find(c => c.type === 'text-cell' && c.title?.toLowerCase().includes('first')),
        lastNameColumn: boardColumns.find(c => c.type === 'text-cell' && c.title?.toLowerCase().includes('last')),
    };
}

/** The display label used for a free-input entry built from raw digits. */
export function selectedContactDisplayName(contact: SelectedContact): string {
    return contact.displayName || contact.name || '';
}
